import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { useTranslation } from '../i18n';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowRightIcon, SparklesIcon, TrophyIcon, UsersIcon, ShieldIcon } from '../components/icons';

export default function HomePage() {
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation();

  const highlights = [
    t('home.highlights.contests'),
    t('home.highlights.prizes'),
    t('home.highlights.timed'),
    t('home.highlights.results'),
    t('home.highlights.admin'),
  ];

  const features = [
    { icon: TrophyIcon, title: t('home.features.skill.title'), body: t('home.features.skill.body') },
    { icon: ShieldIcon, title: t('home.features.scoring.title'), body: t('home.features.scoring.body') },
    { icon: UsersIcon, title: t('home.features.standing.title'), body: t('home.features.standing.body') },
    {
      icon: SparklesIcon,
      title: t('home.features.accounts.title'),
      body: t('home.features.accounts.body'),
    },
  ];

  const steps = [
    { step: '1', title: t('home.steps.one.title'), body: t('home.steps.one.body') },
    { step: '2', title: t('home.steps.two.title'), body: t('home.steps.two.body') },
    { step: '3', title: t('home.steps.three.title'), body: t('home.steps.three.body') },
    { step: '4', title: t('home.steps.four.title'), body: t('home.steps.four.body') },
  ];

  return (
    <div>
      {/* Hero */}
      <section className="border-b border-border bg-surface-muted">
        <div className="mx-auto grid max-w-6xl gap-10 px-4 py-16 sm:py-20 lg:grid-cols-2 lg:items-center">
          <div>
            <Badge tone="brand">{t('app.tagline')}</Badge>
            <h1 className="mt-4 text-4xl font-bold tracking-tight text-fg sm:text-5xl">
              {t('home.hero.title')}
              <span className="block text-brand">{t('home.hero.titleAccent')}</span>
            </h1>
            <p className="mt-5 max-w-xl text-lg text-muted">{t('home.hero.subtitle')}</p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              {isAuthenticated ? (
                <Link to="/dashboard">
                  <Button size="lg">
                    {t('home.hero.goToDashboard')}
                    <ArrowRightIcon className="h-4 w-4" />
                  </Button>
                </Link>
              ) : (
                <>
                  <Link to="/register">
                    <Button size="lg">
                      {t('home.hero.createAccount')}
                      <ArrowRightIcon className="h-4 w-4" />
                    </Button>
                  </Link>
                  <Link to="/login">
                    <Button size="lg" variant="outline">
                      {t('home.hero.signIn')}
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>

          <Card className="lg:w-full lg:justify-self-end">
            <CardHeader>
              <CardTitle>{t('home.highlights.title')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <ul className="space-y-3 text-sm text-muted">
                {highlights.map((item) => (
                  <li key={item} className="flex gap-3">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brand" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="rounded-lg bg-warning-soft px-3 py-2 text-xs text-warning-soft-fg">
                {t('home.highlights.note')}
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Features */}
      <section className="mx-auto max-w-6xl px-4 py-16">
        <h2 className="text-center text-2xl font-semibold tracking-tight text-fg">
          {t('home.features.title')}
        </h2>
        <p className="mx-auto mt-2 max-w-2xl text-center text-muted">
          {t('home.features.subtitle')}
        </p>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {features.map((feature) => (
            <Card key={feature.title}>
              <CardContent className="pt-6">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-soft text-brand-soft-fg">
                  <feature.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 font-semibold text-fg">{feature.title}</h3>
                <p className="mt-2 text-sm text-muted">{feature.body}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="border-y border-border bg-surface">
        <div className="mx-auto max-w-6xl px-4 py-16">
          <h2 className="text-2xl font-semibold tracking-tight text-fg">{t('home.steps.title')}</h2>
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {steps.map((item) => (
              <div key={item.step} className="rounded-xl border border-border p-5">
                <span
                  className="flex h-8 w-8 items-center justify-center rounded-full bg-brand text-sm font-semibold text-brand-fg"
                  aria-hidden="true"
                >
                  {item.step}
                </span>
                <h3 className="mt-4 font-semibold text-fg">{item.title}</h3>
                <p className="mt-2 text-sm text-muted">{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to action */}
      {!isAuthenticated && (
        <section className="mx-auto max-w-6xl px-4 py-16">
          <div className="rounded-2xl border border-border bg-surface px-8 py-12 text-center">
            <h2 className="text-2xl font-semibold text-fg">{t('home.cta.title')}</h2>
            <p className="mx-auto mt-2 max-w-xl text-muted">{t('home.cta.body')}</p>
            <div className="mt-6 flex justify-center">
              <Link to="/register">
                <Button size="lg">
                  {t('home.cta.button')}
                  <ArrowRightIcon className="h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
